import * as _ from 'lodash';
import { useEffect, useRef, useState, CSSProperties } from 'react';
import { IWatrRequest } from '@nkr/models/index';
import { isSelfEmployed } from '../utils/employment-type.utils';
import { FormControl, Button, OverlayTrigger, Glyphicon, Popover } from 'react-bootstrap';
import {
    WEEKLY_CAP,
    calculateAdjustedBaseAmount,
    calculateAdjustedWeeklyReimbursement,
    filterEligibleWeeks,
    getDefaultWeeklyWageBase
} from './wage-computation';
import { formatApproverDateTime } from '../utils/approver-display.utils';
import { formatMoneyInput, parseMoneyInput } from './money-input';
import './week-by-week-breakdown.css';

export { WEEKLY_CAP, calculateAdjustedBaseAmount, calculateAdjustedWeeklyReimbursement, getDefaultWeeklyWageBase } from './wage-computation';

interface IProps {
    data: IWatrRequest;
    allowEditOtherAmount?: boolean;
    onOtherAmountChange?: (weekRequested: number, otherAmount: number) => void;
    draftOtherAmounts?: Record<number, number>;
    overrideWeeklyAmount?: number;
    onSaveOtherAmount?: (weekRequested: number, otherAmount: number) => void | Promise<void>;
    savingWeekRequested?: number | null;
    saveError?: string | null;
    wellStyle?: CSSProperties;
    briefHelp?: boolean;
    showApproverColumns?: boolean;
}

const WeekByWeekBreakdown = (props: IProps) => {
    const [localOtherAmounts, setLocalOtherAmounts] = useState<Record<number, number>>({});
    const [otherFocusWeek, setOtherFocusWeek] = useState<number | null>(null);
    const prevPropAmountsRef = useRef<Record<number, number>>({});
    const requestWeeks = props.data.requestWeeks;

    useEffect(() => {
        if (!requestWeeks || requestWeeks.length === 0) return;
        const eligible = filterEligibleWeeks(props.data);
        let updated = false;
        const next = { ...prevPropAmountsRef.current };
        eligible.forEach(w => {
            const propVal = w.otherAmount ?? 0;
            if (next[w.weekRequested] !== propVal) {
                next[w.weekRequested] = propVal;
                updated = true;
            }
        });
        if (updated) {
            prevPropAmountsRef.current = next;
            setLocalOtherAmounts(prev => ({ ...prev, ...next }));
        }
    }, [requestWeeks]);

    if (!props.data.requestWeeks || props.data.requestWeeks.length === 0) {
        return null;
    }

    const filteredWeeks = filterEligibleWeeks(props.data)
        .sort((a, b) => a.weekRequested - b.weekRequested);

    if (filteredWeeks.length === 0) {
        return null;
    }

    const employmentType = props.data.employmentType;
    const isSelfEmployedValue = isSelfEmployed(employmentType);

    const incomePercentCovered = props.data.incomePercentCovered || 0;
    const rawBaseAmount = props.overrideWeeklyAmount != null && props.overrideWeeklyAmount > 0
        ? props.overrideWeeklyAmount
        : getDefaultWeeklyWageBase(props.data);
    const cappedBaseAmount = Math.min(rawBaseAmount, WEEKLY_CAP);
    const roundedBaseAmount = Math.round(cappedBaseAmount * 100) / 100;
    const adjustedBaseAmount = calculateAdjustedBaseAmount(roundedBaseAmount, incomePercentCovered, WEEKLY_CAP);
    const showApproverColumns = props.showApproverColumns === true;
    const otherAmountEps = 0.005;
    const pendingOtherWeeks = filteredWeeks
        .filter(week => {
            const canEdit = props.allowEditOtherAmount && props.onOtherAmountChange;
            const isControlled = props.draftOtherAmounts !== undefined;
            const current = canEdit
                ? (isControlled ? (props.draftOtherAmounts![week.weekRequested] ?? week.otherAmount ?? 0) : (localOtherAmounts[week.weekRequested] ?? week.otherAmount ?? 0))
                : (week.otherAmount || 0);

            return canEdit && Math.abs(current - (week.otherAmount ?? 0)) >= otherAmountEps;
        })
        .map(week => week.weekRequested);

    return (
        <div className="watr-week-breakdown" style={props.wellStyle}>
            <div className="watr-week-breakdown-head">Weekly wages</div>
            <div className={showApproverColumns ? 'watr-week-breakdown-scroll' : undefined}>
                <table
                    className="watr-week-breakdown-table"
                    style={showApproverColumns ? { tableLayout: 'fixed' } : undefined}
                >
                    <thead>
                        <tr>
                            <th style={showApproverColumns ? { width: '11%' } : undefined}>Week</th>
                            <th className="amt" style={showApproverColumns ? { width: '14%' } : undefined}>Base</th>
                            <th className="amt" style={showApproverColumns ? { width: '28%' } : undefined}>
                                Other Amount
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement="top"
                                    overlay={
                                        <Popover id="popover-other-amount-header" title="Other Amount">
                                            Other Amount is the offset: other income for this week (second job, PTO, etc.). It is not the donor’s wage—that is Base. Saving it lowers Adjusted for this week’s payment.
                                        </Popover>
                                    }
                                >
                                    <span className="watr-week-breakdown-help">
                                        <Glyphicon glyph="info-sign" aria-label="Other Amount help" />
                                    </span>
                                </OverlayTrigger>
                            </th>
                            <th className="amt" style={showApproverColumns ? { width: '14%' } : undefined}>Adjusted</th>
                            {showApproverColumns && (
                                <th className="amt" style={{ width: '33%' }}>Approver</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredWeeks.map((week, index) => {
                            const canEdit = props.allowEditOtherAmount && props.onOtherAmountChange;
                            const isControlled = props.draftOtherAmounts !== undefined;
                            const otherAmount = canEdit
                                ? (isControlled ? (props.draftOtherAmounts![week.weekRequested] ?? week.otherAmount ?? 0) : (localOtherAmounts[week.weekRequested] ?? week.otherAmount ?? 0))
                                : (week.otherAmount || 0);
                            const adjustedAmount = calculateAdjustedWeeklyReimbursement(adjustedBaseAmount, otherAmount, WEEKLY_CAP);
                            const approverName = (week.approverName || '').trim();
                            const approvedText = formatApproverDateTime(week.approvedDateTime, true);
                            const isPending = canEdit && Math.abs(otherAmount - (week.otherAmount ?? 0)) >= otherAmountEps;

                            return (
                                <tr key={`${week.requestId}-${week.weekRequested}-${index}`} className={isPending ? 'is-pending' : undefined}>
                                    <td className="wk">{showApproverColumns ? week.weekRequested : `Week ${week.weekRequested}`}</td>
                                    <td className="amt">${adjustedBaseAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="amt">
                                        {canEdit ? (
                                            <div className="watr-week-breakdown-other">
                                                <div className="input-group oi">
                                                    <span className="input-group-addon">$</span>
                                                    <FormControl
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={otherFocusWeek === week.weekRequested
                                                            ? (otherAmount ? String(otherAmount) : '')
                                                            : formatMoneyInput(otherAmount || 0)}
                                                        onFocus={() => setOtherFocusWeek(week.weekRequested)}
                                                        onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => {
                                                            const value = parseMoneyInput((e.target as HTMLInputElement).value);
                                                            const num = Math.max(0, Math.min(value, adjustedBaseAmount));

                                                            if (!isControlled) {
                                                                setLocalOtherAmounts(prev => ({ ...prev, [week.weekRequested]: num }));
                                                            }

                                                            props.onOtherAmountChange!(week.weekRequested, num);
                                                        }}
                                                        onBlur={() => {
                                                            setOtherFocusWeek(null);
                                                            const committed = Math.max(0, Math.min(otherAmount, adjustedBaseAmount));

                                                            if (!isControlled) {
                                                                setLocalOtherAmounts(prev => ({ ...prev, [week.weekRequested]: committed }));
                                                            }

                                                            props.onOtherAmountChange!(week.weekRequested, committed);
                                                        }}
                                                    />
                                                </div>
                                                {props.onSaveOtherAmount && (
                                                    <Button
                                                        type="button"
                                                        bsStyle={isPending ? 'primary' : 'default'}
                                                        bsSize="sm"
                                                        className="watr-week-breakdown-save"
                                                        title={isPending ? 'Save Other Amount for this week' : 'No Other Amount change to save'}
                                                        aria-label={isPending ? 'Save Other Amount for this week' : 'No Other Amount change to save'}
                                                        disabled={!isPending || props.savingWeekRequested === week.weekRequested}
                                                        onClick={() => {
                                                            const committed = Math.max(0, Math.min(otherAmount, adjustedBaseAmount));

                                                            if (!isControlled) {
                                                                setLocalOtherAmounts(prev => ({ ...prev, [week.weekRequested]: committed }));
                                                            }

                                                            props.onOtherAmountChange!(week.weekRequested, committed);
                                                            props.onSaveOtherAmount!(week.weekRequested, committed);
                                                        }}
                                                    >
                                                        <Glyphicon glyph={props.savingWeekRequested === week.weekRequested ? 'refresh' : 'ok'} />
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            `$${otherAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                        )}
                                    </td>
                                    <td className="adj">${adjustedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    {showApproverColumns && (
                                        <td>
                                            {approverName || approvedText ? (
                                                <div className="watr-week-breakdown-approver">
                                                    <div className="watr-week-breakdown-approver-name">{approverName || '—'}</div>
                                                    {approvedText && (
                                                        <div className="watr-week-breakdown-approver-date">{approvedText}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="watr-week-breakdown-approver-date">—</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="watr-week-breakdown-formula">
                <p>
                    <strong>Other Amount</strong> is the offset: other income for that week, not the wage. Leave $0 unless the donor was paid elsewhere.
                    Saving it subtracts from Base. <strong>Adjusted</strong> is what this payment uses (cap $2,000/week).
                </p>
                {props.briefHelp ? null : (
                    <p>
                        Base = {isSelfEmployedValue ? 'Tax Gross ÷ 52' : 'Paystub-derived weekly'}
                        {incomePercentCovered > 0 ? ` · ${incomePercentCovered}% covered` : ''}.
                    </p>
                )}
                {props.allowEditOtherAmount && props.onSaveOtherAmount && (
                    <p>Enter Other Amount, then click the check to save and confirm.</p>
                )}
                {pendingOtherWeeks.length > 0 && (
                    <p className="watr-week-breakdown-pending-msg">
                        Unsaved Other Amount on week{pendingOtherWeeks.length !== 1 ? 's' : ''} {pendingOtherWeeks.join(', ')}. Save with the check before recording reimbursement.
                    </p>
                )}
                {props.allowEditOtherAmount && props.saveError && (
                    <p className="watr-week-breakdown-error">{props.saveError}</p>
                )}
            </div>
        </div>
    );
};

export default WeekByWeekBreakdown;